import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import "./styles.css";

export interface DatePickerProps {
  label?: string;
  value?: string; // YYYY-MM-DD
  onChange?: (value: string) => void;
  error?: string;
  min?: string; // YYYY-MM-DD
  max?: string; // YYYY-MM-DD
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseDate(str: string): Date | null {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDisplay(str: string): string {
  const date = parseDate(str);
  if (!date) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value = "",
  onChange,
  error,
  min,
  max,
  placeholder = "Select date",
  disabled = false,
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => parseDate(value), [value]);
  const minDate = useMemo(() => parseDate(min ?? ""), [min]);
  const maxDate = useMemo(() => parseDate(max ?? ""), [max]);

  const [viewYear, setViewYear] = useState(() => (selected ?? new Date()).getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (selected ?? new Date()).getMonth());

  // Sync view to selected when picker opens
  useEffect(() => {
    if (open && selected) {
      setViewYear(selected.getFullYear());
      setViewMonth(selected.getMonth());
    }
  }, [open, selected]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const isDisabledDate = (date: Date): boolean => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const handleSelect = (date: Date) => {
    if (isDisabledDate(date)) return;
    onChange?.(formatDate(date));
    setOpen(false);
  };

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const startDay = firstDay.getDay(); // 0 = Sunday
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const cells: { date: Date; currentMonth: boolean }[] = [];

    // Previous month trailing days
    for (let i = startDay - 1; i >= 0; i--) {
      cells.push({
        date: new Date(viewYear, viewMonth - 1, daysInPrevMonth - i),
        currentMonth: false,
      });
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({
        date: new Date(viewYear, viewMonth, d),
        currentMonth: true,
      });
    }

    // Next month leading days (fill to 42 = 6 rows)
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      cells.push({
        date: new Date(viewYear, viewMonth + 1, d),
        currentMonth: false,
      });
    }

    return cells;
  }, [viewYear, viewMonth]);

  const today = useMemo(() => new Date(), []);

  return (
    <div
      className={`datepicker-container ${error ? "datepicker-container--error" : ""}`}
      ref={containerRef}
    >
      {label && (
        <label className="datepicker-label">
          {label}
          {required && <span className="datepicker-required"> *</span>}
        </label>
      )}
      <button
        type="button"
        className={`datepicker-trigger ${open ? "datepicker-trigger--active" : ""} ${disabled ? "datepicker-trigger--disabled" : ""}`}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        <Calendar size={16} className="datepicker-trigger-icon" />
        <span className={`datepicker-trigger-text ${!value ? "datepicker-trigger-text--placeholder" : ""}`}>
          {value ? formatDisplay(value) : placeholder}
        </span>
      </button>
      {error && <span className="datepicker-error">{error}</span>}

      {open && (
        <div className="datepicker-dropdown">
          {/* Header */}
          <div className="datepicker-header">
            <button type="button" className="datepicker-nav-btn" onClick={goToPrevMonth}>
              <ChevronLeft size={16} />
            </button>
            <span className="datepicker-header-title">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button type="button" className="datepicker-nav-btn" onClick={goToNextMonth}>
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day names */}
          <div className="datepicker-weekdays">
            {DAYS.map((d) => (
              <span key={d} className="datepicker-weekday">{d}</span>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="datepicker-grid">
            {calendarDays.map((cell, i) => {
              const isSelected = selected && isSameDay(cell.date, selected);
              const isToday = isSameDay(cell.date, today);
              const isOtherMonth = !cell.currentMonth;
              const isOff = isDisabledDate(cell.date);

              return (
                <button
                  key={i}
                  type="button"
                  className={[
                    "datepicker-day",
                    isSelected && "datepicker-day--selected",
                    isToday && !isSelected && "datepicker-day--today",
                    isOtherMonth && "datepicker-day--other",
                    isOff && "datepicker-day--disabled",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleSelect(cell.date)}
                  disabled={isOff}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
